import { useEffect, useRef, useState } from 'react';

import { Icon } from './Icon';
import { VideoTile } from './VideoTile';
import { initials } from '../lib/format';
import type { ParticipantView } from '../lib/livekit';
import './PresentationStage.css';

/**
 * Раскладка режима презентации.
 *
 * ── Чем отличается от обычной ───────────────────────────────────────
 *
 * Обычная раскладка старается показать всех: демонстрация занимает
 * сцену, лица уходят в ленту снизу. Для доклада это неверный компромисс
 * — лента съедает нижнюю треть экрана, а на слайде именно там обычно и
 * находится самое мелкое.
 *
 * Здесь показываемое занимает кадр целиком, а докладчик появляется
 * поверх маленьким окном — и только когда говорит. В паузах окно
 * уходит, и слайд снова виден полностью.
 *
 * ── Почему окно не мигает ───────────────────────────────────────────
 *
 * `isSpeaking` дёргается на каждой паузе между словами. Если вешать
 * появление прямо на него, окно будет мерцать в такт речи. Поэтому
 * появление мгновенное, а уход — только после `HIDE_AFTER_MS` тишины:
 * докладчик договорил мысль, выдержал паузу, и окно спокойно ушло.
 */
const HIDE_AFTER_MS = 4000;

export function PresentationStage({ participants }: { participants: ParticipantView[] }) {
  const sharing = participants.find((p) => p.screenShareEnabled);

  /*
   * Кого показывать в окне.
   *
   * Обычно это организатор — он ведёт встречу. Но если показывает
   * кто-то другой (организатор передал слово), в окне логичнее видеть
   * того, кто говорит по своим же слайдам. Поэтому приоритет такой:
   * тот, кто демонстрирует → организатор → никто.
   */
  const host = participants.find((p) => p.isHost) ?? null;
  const speaker = sharing && !sharing.isLocal ? sharing : host;

  const visible = useSpeakingWindow(speaker?.isSpeaking ?? false, Boolean(sharing));

  return (
    <div className="present">
      <div className="present__main">
        {sharing ? (
          <VideoTile participant={sharing} source="screen" featured />
        ) : (
          <WaitingForShare host={host} />
        )}
      </div>

      {speaker && sharing && (
        <div
          className={`present__pip ${visible ? 'present__pip--on' : ''}`}
          aria-hidden={!visible}
        >
          <VideoTile participant={speaker} />
        </div>
      )}
    </div>
  );
}

/**
 * Пока показа нет, экран не должен быть пустым.
 *
 * Показываем организатора крупно и говорим, чего ждём: иначе участник
 * видит чёрное поле и не понимает, сломалось что-то или ещё не начали.
 */
function WaitingForShare({ host }: { host: ParticipantView | null }) {
  return (
    <div className="present__await">
      <div className="present__await-face">
        {host ? (
          <VideoTile participant={host} featured />
        ) : (
          <div className="present__await-blank">
            <span className="present__await-initials">{initials('GTO CONNECT')}</span>
          </div>
        )}
      </div>
      <p className="present__await-text">
        <Icon name="present" size={16} />
        Режим презентации включён. Ждём начала демонстрации.
      </p>
    </div>
  );
}

/** Появляется сразу, уходит после паузы — см. комментарий к HIDE_AFTER_MS. */
function useSpeakingWindow(speaking: boolean, enabled: boolean): boolean {
  const [visible, setVisible] = useState(false);
  const timer = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      return;
    }

    if (speaking) {
      window.clearTimeout(timer.current);
      setVisible(true);
      return;
    }

    timer.current = window.setTimeout(() => setVisible(false), HIDE_AFTER_MS);
    return () => window.clearTimeout(timer.current);
  }, [speaking, enabled]);

  // Уходим со страницы с работающим таймером — чистим, иначе React
  // отругается на попытку обновить размонтированный компонент.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return visible;
}
